import { ButtonColors, ButtonSizes, SecondaryButton } from 'src/shared/design-system/Buttons';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Border, Colors, Font, Mixins, Spacing, Transition } from 'web-common/src/shared/styles';
import { PHOTO_SIZE } from './consts';

interface IPhotoProps {
  disabled?: boolean;
  error?: boolean;
  isStatic?: boolean;
  photoSize?: number;
  readOnly?: boolean;
  rounded?: boolean;
}

export const getPhotoSize = ({ disabled, isStatic, photoSize, rounded }: IPhotoProps) => `
  border-radius: ${rounded ? '100%' : '0%'};
  cursor: ${() => {
    if (isStatic) {
      return 'default';
    }
    return disabled ? 'not-allowed' : 'pointer';
  }};
  max-width: ${photoSize || PHOTO_SIZE}px;
  width: 100%;

  &:after {
    content: "";
    display: block;
    padding-top: 100%;
  }
`;

export const Photo = styled.img<IPhotoProps>`
  border: ${Border.Base} ${Colors.GRAY_LITE_10} !important;
  ${getPhotoSize};
`;

export const PhotoContainer = styled.div<IPhotoProps>`
  background: ${Colors.GRAY_LITE_20};
  border: ${Border.Base} ${({ error }) => (error ? Colors.RADISH : Colors.GRAY_LITE_10)};
  color: ${Colors.GRAY};
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: ${({ photoSize }) => photoSize || PHOTO_SIZE}px;
  transition: background ${Transition.Time.BASE}, border ${Transition.Time.BASE};
  overflow: hidden;
  ${getPhotoSize};

  & > ${Photo} {
    border: 0px !important;
    border-radius: 0px !important;
    min-width: ${({ photoSize }) => photoSize || PHOTO_SIZE}px;
    width: auto;
    height: 100%;
    object-fit: cover;
  }

  ${ifProp(
    'readOnly',
    css`
      + button {
        text-decoration: none !important;
      }
    `
  )}
`;

export const UploadButton = styled(SecondaryButton).attrs(({ color }) => ({
  fluid: true,
  color: color ?? ButtonColors.BLUE,
  size: ButtonSizes.M,
}))<{ photoSize?: number; readOnly?: boolean }>`
  max-width: calc(${({ photoSize }) => photoSize || PHOTO_SIZE}px + ${Spacing.QUADRUPLE});
  width: 100% !important;
  ${({ disabled, readOnly }) =>
    match(disabled)
      .with(
        true,
        () => css`
          cursor: 'not-allowed' !important;
          text-decoration: ${readOnly ? 'default' : 'line-through'};
        `
      )
      .otherwise(
        () => css`
          cursor: ${readOnly ? 'default' : 'pointer'} !important;
          text-decoration: none;
        `
      )}
`;

export const MediaDropzone = styled.div`
  box-sizing: border-box;
  display: flex;
  padding: 10px;
  height: 100px;
  width: 100%;
  border: 2px dashed ${Colors.GRAY_DARK_10};
  transition: background ${Transition.Time.BASE}, border ${Transition.Time.BASE};
  cursor: pointer;

  ${Mixins.Flex.center()}

  * {
    margin: 0;
  }
`;

export const Container = styled.div<{ isDragAccept?: boolean; isDragReject?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 100%;
  gap: ${Spacing.SINGLE};

  ${ifProp(
    'isDragReject',
    css`
      & > ${PhotoContainer} {
        background: ${Colors.BELL_PEPPER_LITE_10};
        border-color: ${Colors.RADISH};
      }
      & > ${MediaDropzone} {
        background: ${Colors.BELL_PEPPER_LITE_30};
        border-color: ${Colors.RADISH};
      }
      & > button {
        color: ${Colors.RADISH};
        border-color: ${Colors.RADISH};
      }
    `
  )}

  ${ifProp(
    'isDragAccept',
    css`
      & > ${PhotoContainer} {
        background: ${Colors.AQUA_LITE_10};
        border-color: ${Colors.AQUA};
      }
      & > ${MediaDropzone} {
        background: ${Colors.AQUA_LITE_30};
        border-color: ${Colors.AQUA};
      }
    `
  )}
`;

export const UploadDropZoneContainer = styled.div`
  color: ${Colors.GRAY_DARK_10};
  font-size: ${Font.Size.L};
  font-weight: 600;
  line-height: ${Font.LineHeight.M};
  padding: ${Spacing.DOUBLE};
  text-align: center;
`;

export const UploadDropdownReplaceText1 = styled.span`
  font-weight: 400;
`;

export const UploadDropdownReplaceText2 = styled.span`
  color: ${Colors.AQUA};
`;
